import React, { useRef, useCallback, useMemo } from "react";
import {
  Box,
  TextField,
  FormHelperText,
  FormControl,
  FormLabel,
} from "@mui/material";
import PropTypes from "prop-types";
const DEFAULT_LENGTH = 4;
const NUMERIC_REGEX = /^\d*$/;
const NUMERIC_PASTE_REGEX = /^\d+$/;
const PRIMARY_COLOR = "rgba(26, 64, 125, 1)";

const padDigits = (value, length) => {
  const sliced = (value || "").slice(0, length).split("");

  while (sliced.length < length) {
    sliced.push("");
  }

  return sliced;
};

const DigitBoxInput = ({
  value,
  onChange,
  length = DEFAULT_LENGTH,
  error = false,
  helperText,
  disabled = false,
  autoFocus = false,
  id,
  label,
  required = false,
  ariaLabel,
  ariaDescribedBy,
}) => {
  const inputsRef = useRef([]);

  const digits = padDigits(value, length);

  const focusInput = useCallback((index) => {
    inputsRef.current[index]?.focus();
  }, []);

  const emitChange = useCallback(
    (next) => {
      onChange(next.join(""));
    },
    [onChange]
  );

  const handleChange = (nextValue, index) => {
    if (!NUMERIC_REGEX.test(nextValue)) return;

    const truncated = nextValue.slice(-1);

    const updated = [...digits];
    updated[index] = truncated;

    emitChange(updated);

    if (truncated && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key !== "Backspace") return;

    const updated = [...digits];

    // Current box contains value
    if (digits[index]) {
      updated[index] = "";
      emitChange(updated);
      return;
    }

    // Current box empty -> move to previous
    if (index > 0) {
      updated[index - 1] = "";
      emitChange(updated);
      focusInput(index - 1);
    }
  };

  const handlePaste = (e, startIndex) => {
    e.preventDefault();

    const pasted = e.clipboardData.getData("text").trim();

    if (!NUMERIC_PASTE_REGEX.test(pasted)) return;

    const pastedDigits = pasted
      .slice(0, length - startIndex)
      .split("");

    const updated = [...digits];

    pastedDigits.forEach((digit, offset) => {
      updated[startIndex + offset] = digit;
    });

    emitChange(updated);

    const nextFocus = Math.min(
      startIndex + pastedDigits.length,
      length - 1
    );

    focusInput(nextFocus);
  };

  const borderColor = error ? "#d32f2f" : "#D1D5DB";
  const focusBorderColor = error ? "#d32f2f" : PRIMARY_COLOR;

  const helperId = id ? `${id}-helper-text` : undefined;

  const describedBy =
    [helperText ? helperId : undefined, ariaDescribedBy]
      .filter(Boolean)
      .join(" ") || undefined;

  const digitKeys = useMemo(
    () =>
      Array.from(
        { length },
        (_, index) => `digit-box-${index}`
      ),
    [length]
  );

  return (
    <FormControl fullWidth>
      {label && (
        <FormLabel
          className="form-label"
          htmlFor={id}
          sx={{
            color: "#1C252E",
            fontSize: {
              xs: "13px",
              sm: "13px",
              md: "14px",
              xl: "14px",
            },
            fontWeight: "500",
            lineHeight: "22px",
            mb: "6px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div className="addressProofSpan">
            <div>
              {label}
              {required && (
                <span className="asteriskColor"> *</span>
              )}
            </div>
          </div>
        </FormLabel>
      )}

      <Box
        id={id}
        role="group"
        aria-label={ariaLabel}
      >
        <Box
          sx={{
            display: "flex",
            gap: "8px",
          }}
        >
          {digits.map((digit, index) => (
            <TextField
              key={digitKeys[index]}
              inputRef={(element) => {
                inputsRef.current[index] = element;
              }}
              value={digit}
              onChange={(e) =>
                handleChange(e.target.value, index)
              }
              onKeyDown={(e) =>
                handleKeyDown(e, index)
              }
              onPaste={(e) =>
                handlePaste(e, index)
              }
              disabled={disabled}
              autoFocus={autoFocus && index === 0}
              error={error}
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
                pattern: "[0-9]*",
                "aria-label": ariaLabel
                  ? `${ariaLabel} digit ${index + 1}`
                  : `Digit ${index + 1}`,
                "aria-describedby": describedBy,
                style: {
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: 500,
                  padding: "8px 0",
                },
              }}
              variant="outlined"
              sx={{
                width: {
                  xs: "42px",
                  sm: "48px",
                },
                height: "44px",

                "& .MuiOutlinedInput-root": {
                  height: "100%",
                  borderRadius: "8px",

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: focusBorderColor,
                    borderWidth: "2px",
                  },
                },
              }}
            />
          ))}
        </Box>

        {helperText && (
          <FormHelperText
            id={helperId}
            error={error}
            sx={{
              mt: 0.5,
              ml: 0,
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </Box>
    </FormControl>
  );
};

export default DigitBoxInput;

DigitBoxInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  length: PropTypes.number,
  error: PropTypes.bool,
  helperText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
};